import React, { useState, useEffect, useCallback } from "react";
import CoachPlansSkeleton from "../../components/layout/CoachPlansSkeleton";
import api from "../../api/api";
import { useTheme } from "../../hooks/useTheme";
import { useAuth } from "../../hooks/useAuth";
import { useNavigate } from "react-router-dom";
import { Ticket, Loader2, ArrowLeft } from "lucide-react";
import CoachPlanCards from "../../components/CoachPlanCards";

export default function CoachPlans() {
    const { isDarkMode } = useTheme();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [code, setCode] = useState("");
    const [redeeming, setRedeeming] = useState(false);
    const [subscribing, setSubscribing] = useState(null);
    const [message, setMessage] = useState(null);
    const [currentTierId, setCurrentTierId] = useState(null);
    // Preview only — a code grants nothing until a plan is confirmed below.
    const [promo, setPromo] = useState(null); // {code, discountType, discountValue, tiers}

    useEffect(() => {
        if (!user?.isCoach && !user?.isAdmin) navigate("/userdashboard");
    }, [user, navigate]);

    const fetchPlans = useCallback(async () => {
        try {
            setLoading(true);
            // Fetch the plans and the coach's own membership together so the
            // current plan is highlighted. A coach with no membership row is on
            // the free tier by default, so fall back to the free plan.
            const [plansRes, memRes] = await Promise.allSettled([
                api.get("/coach/plans"),
                api.get("/coach/my-membership"),
            ]);
            const plansData = plansRes.status === "fulfilled" ? (plansRes.value.data.plans || []) : [];
            setPlans(plansData);
            const m = memRes.status === "fulfilled" ? memRes.value.data.membership : null;
            if (m?.isActive && m?.TierID) {
                setCurrentTierId(m.TierID);
            } else {
                const freePlan = plansData.find((pl) => Number(pl.Price) === 0 && !pl.TrialDays);
                setCurrentTierId(freePlan ? freePlan.TierID : null);
            }
        } catch {
            setPlans([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchPlans(); }, [fetchPlans]);

    const showMsg = (text, type = "success") => {
        setMessage({ text, type });
        setTimeout(() => setMessage(null), 5000);
    };

    const describeDiscount = (p) => {
        if (!p) return "";
        if (p.discountType === "free") return "free";
        if (p.discountType === "percent") return `${Number(p.discountValue)}% off`;
        return `$${Number(p.discountValue || 0).toFixed(2)} off`;
    };

    // The promo's effect on one plan, or null when this plan isn't covered.
    const promoFor = (tierId) => promo?.tiers?.find((t) => t.TierID === tierId) || null;

    // Applying a code only previews it: the plans below re-price, nothing is
    // granted and the code's redemption count is untouched until a plan is
    // confirmed.
    const handleRedeem = async () => {
        const trimmed = code.trim().toUpperCase();
        if (!trimmed) return;
        try {
            setRedeeming(true);
            const res = await api.post("/coach/code/preview", { code: trimmed });
            setPromo(res.data);
            const n = res.data?.tiers?.length || 0;
            showMsg(`${trimmed} applied — ${describeDiscount(res.data)} on ${n} plan${n === 1 ? "" : "s"} below.`, "success");
        } catch (err) {
            showMsg(err.response?.data?.error || "That code isn't valid or has expired.", "error");
        } finally {
            setRedeeming(false);
        }
    };

    const clearPromo = () => {
        setPromo(null);
        setCode("");
    };

    const handleSubscribe = async (plan) => {
        const isFree = Number(plan.Price) === 0 && !plan.TrialDays;
        try {
            setSubscribing(plan.TierID);
            // A 'free' (full grant) code covering this plan: this confirmation is
            // what actually claims it — the code alone granted nothing.
            if (promo && promo.discountType === "free" && promoFor(plan.TierID)) {
                await api.post("/coach/redeem-code", { code: promo.code, tier_id: plan.TierID });
                showMsg("Plan activated.", "success");
                setTimeout(() => navigate("/coaching/membership"), 900);
                return;
            }
            if (isFree) {
                await api.post("/coach/select-free-plan", { tier_id: plan.TierID });
                showMsg("Plan activated.", "success");
                setTimeout(() => navigate("/coaching/membership"), 900);
                return;
            }
            const res = await api.post("/coach/create-checkout-session", {
                price_id: plan.PaymentLink,
                tier_id: plan.TierID,
            });
            if (res.data.checkout_url) window.location.href = res.data.checkout_url;
            else { showMsg("Could not start checkout.", "error"); setSubscribing(null); }
        } catch (err) {
            showMsg(err.response?.data?.error || "Could not start checkout.", "error");
            setSubscribing(null);
        }
    };

    const heading = isDarkMode ? "text-slate-100" : "text-slate-900";
    const sub = isDarkMode ? "text-slate-400" : "text-slate-600";
    const card = isDarkMode ? "bg-slate-800 border-slate-700" : "bg-white border-slate-200";

    if (loading) return <CoachPlansSkeleton isDarkMode={isDarkMode} />;

    return (
        <div className={`min-h-screen ${isDarkMode ? "bg-surface" : "bg-surface"}`}>
            <div className="max-w-5xl mx-auto px-4 py-10">
                <button onClick={() => navigate("/coaching/membership")} className={`inline-flex items-center gap-1.5 text-sm font-semibold mb-6 ${sub} hover:underline`}>
                    <ArrowLeft size={16} /> Back to My Membership
                </button>

                <header className="text-center mb-8">
                    <h1 className={`text-3xl font-extrabold tracking-tight mb-1 ${heading}`}>Choose a plan</h1>
                    <p className={sub}>Upgrade your coach membership to unlock premium tools.</p>
                </header>

                {message && (
                    <div role="alert" className={`mb-6 flex items-center gap-3 p-3.5 rounded-xl text-sm font-medium ${
                        message.type === "error"
                            ? isDarkMode ? "bg-red-900/30 text-red-300 border border-red-800/50" : "bg-red-50 text-red-800 border border-red-200"
                            : isDarkMode ? "bg-primary-900/30 text-primary-300 border border-primary-800/50" : "bg-primary-50 text-primary-800 border border-primary-200"
                    }`}>{message.text}</div>
                )}

                {/* Referral code */}
                <div className={`rounded-2xl border shadow-sm p-5 mb-6 ${card}`}>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                        <div className="flex items-center gap-3 flex-1">
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isDarkMode ? "bg-slate-700 text-primary-400" : "bg-primary-50 text-primary-600"}`}>
                                <Ticket size={20} />
                            </div>
                            <div>
                                <h2 className={`font-bold ${heading}`}>Have a referral code?</h2>
                                <p className={`text-sm ${sub}`}>Enter it to unlock or discount your membership.</p>
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <input
                                type="text"
                                value={code}
                                onChange={(e) => setCode(e.target.value.toUpperCase())}
                                onKeyDown={(e) => { if (e.key === "Enter") handleRedeem(); }}
                                disabled={!!promo}
                                placeholder="Enter code"
                                className={`flex-1 sm:w-48 p-2.5 border rounded-lg font-mono uppercase ${isDarkMode ? "bg-slate-900 border-slate-600 text-slate-200 placeholder-slate-500" : "border-slate-300"}`}
                            />
                            {promo ? (
                                <button
                                    onClick={clearPromo}
                                    className={`inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg font-semibold border transition-colors ${isDarkMode ? "border-slate-600 text-slate-200 hover:bg-slate-700" : "border-slate-300 text-slate-700 hover:bg-slate-50"}`}
                                >
                                    Remove
                                </button>
                            ) : (
                                <button
                                    onClick={handleRedeem}
                                    disabled={redeeming || !code.trim()}
                                    className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 disabled:opacity-50 transition-colors"
                                >
                                    {redeeming ? <Loader2 size={18} className="animate-spin" /> : <Ticket size={18} />}
                                    {redeeming ? "Applying..." : "Apply"}
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Plans */}
                <CoachPlanCards plans={plans} onChoose={handleSubscribe} busyTierId={subscribing} currentTierId={currentTierId} promo={promo} />
            </div>
        </div>
    );
}
